/**
 * Admin — Feed visibility rule manager.
 *
 * Quản lý duy nhất một active rule lọc public insight feed. Form hỗ trợ
 * 2 mode: Visual builder (clickable AND/OR/NOT tree) và JSON raw (cho ops
 * power-user). Mỗi rule có thể preview dry-run trước khi activate.
 */

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { adminApi } from '../../utils/api';
import Button from './shared/Button';
import Input from './shared/Input';
import Textarea from './shared/Textarea';
import Spinner from './shared/Spinner';
import Alert from './shared/Alert';
import Modal from './shared/Modal';
import Badge from './shared/Badge';

const SchemaContext = createContext({ fields: [], fieldByName: {}, operators: {} });

const MATCH_ALL_PREDICATE = { op: 'and', args: [] };

function defaultLeaf(schema) {
    const first = schema?.fields?.[0];
    if (!first) return { op: 'eq', field: 'status', value: '' };
    const op = (first.allowed_ops || []).includes('eq') ? 'eq' : (first.allowed_ops?.[0] || 'eq');
    return { op, field: first.name, value: '' };
}

function defaultGroup(op = 'and') {
    return { op, args: [] };
}

function buildFieldByName(fields) {
    return Object.fromEntries((fields || []).map((f) => [f.name, f]));
}

export default function AdminFeedVisibility() {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [editing, setEditing] = useState(null);
    const [auditFor, setAuditFor] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            const data = await adminApi.listFeedVisibilityRules({ limit: 100 });
            setRules(Array.isArray(data) ? data : []);
            setError(null);
        } catch (e) {
            setError(e?.message || 'Không tải được danh sách rule');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);
    useEffect(() => {
        if (!success) return;
        const t = setTimeout(() => setSuccess(null), 4000);
        return () => clearTimeout(t);
    }, [success]);

    const activeRule = useMemo(() => rules.find((r) => r.is_active) || null, [rules]);

    const handleCreate = () => setEditing({
        _mode: 'create',
        name: '',
        description: '',
        predicate: { ...MATCH_ALL_PREDICATE },
        activate: false,
    });

    const handleEdit = (rule) => setEditing({
        _mode: 'edit',
        id: rule.id,
        name: rule.name,
        description: rule.description || '',
        predicate: rule.predicate || { ...MATCH_ALL_PREDICATE },
        is_active: rule.is_active,
    });

    const handleActivate = async (rule) => {
        if (!confirm(`Kích hoạt rule "${rule.name}"?\nRule hiện tại "${activeRule?.name || '(không có)'}" sẽ tự deactivate.`)) return;
        try {
            await adminApi.activateFeedVisibilityRule(rule.id);
            setSuccess(`Đã kích hoạt "${rule.name}"`);
            await load();
        } catch (e) {
            setError(e?.message || 'Kích hoạt thất bại');
        }
    };

    const handleSave = async (form) => {
        try {
            if (form._mode === 'create') {
                await adminApi.createFeedVisibilityRule({
                    name: form.name.trim(),
                    description: form.description.trim() || null,
                    predicate: form.predicate,
                    activate: !!form.activate,
                });
                setSuccess(`Đã tạo "${form.name}"`);
            } else {
                await adminApi.updateFeedVisibilityRule(form.id, {
                    name: form.name.trim(),
                    description: form.description.trim() || null,
                    predicate: form.predicate,
                });
                setSuccess(`Đã cập nhật "${form.name}"`);
            }
            setEditing(null);
            await load();
        } catch (e) {
            setError(e?.message || 'Lưu thất bại');
        }
    };

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Quy tắc hiển thị feed</h1>
                    <p className="text-xs text-gray-500 mt-1">
                        Chỉ một rule được active. Rule active lọc mọi insight hiển thị ở trang public feed.
                    </p>
                </div>
                <Button onClick={handleCreate}>Tạo rule mới</Button>
            </div>

            {error && <Alert variant="error" className="mb-4" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert variant="success" className="mb-4">{success}</Alert>}

            {loading ? (
                <Spinner size="lg" text="Đang tải..." center />
            ) : rules.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                    Chưa có rule nào. Tạo rule mới để bắt đầu.
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto shadow-sm">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Tên</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Mô tả</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Version</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Trạng thái</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Cập nhật</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Thao tác</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {rules.map((rule) => (
                                <tr key={rule.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-medium text-gray-900">{rule.name}</td>
                                    <td className="px-4 py-3 text-gray-600 text-xs max-w-md truncate" title={rule.description || ''}>
                                        {rule.description || '-'}
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono">v{rule.rule_version}</td>
                                    <td className="px-4 py-3">
                                        <Badge variant={rule.is_active ? 'success' : 'secondary'}>
                                            {rule.is_active ? 'Đang dùng' : 'Tạm ngưng'}
                                        </Badge>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-gray-500">
                                        {rule.updated_at ? new Date(rule.updated_at).toLocaleString('vi-VN') : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        <Button variant="ghost" size="xs" onClick={() => handleEdit(rule)}>Sửa</Button>
                                        <Button variant="ghost" size="xs" onClick={() => setAuditFor(rule)}>Lịch sử</Button>
                                        {!rule.is_active && (
                                            <Button variant="ghost" size="xs" className="text-green-600"
                                                onClick={() => handleActivate(rule)}>
                                                Kích hoạt
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && (
                <RuleEditorModal
                    initial={editing}
                    onSave={handleSave}
                    onClose={() => setEditing(null)}
                />
            )}

            {auditFor && (
                <AuditLogModal
                    rule={auditFor}
                    onClose={() => setAuditFor(null)}
                />
            )}
        </div>
    );
}


const ACTION_BADGES = {
    create: { variant: 'success', label: 'Tạo mới' },
    update: { variant: 'primary', label: 'Cập nhật' },
    activate: { variant: 'success', label: 'Kích hoạt' },
    deactivate: { variant: 'secondary', label: 'Tạm ngưng' },
};


function AuditLogModal({ rule, onClose }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const data = await adminApi.listFeedVisibilityAudit(rule.id, { limit: 100 });
                if (!cancelled) setEntries(Array.isArray(data) ? data : []);
            } catch (e) {
                if (!cancelled) setError(e?.message || 'Không tải được lịch sử');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [rule.id]);

    const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

    return (
        <Modal open title={`Lịch sử — ${rule.name}`} onClose={onClose} size="xl">
            <div className="p-5 space-y-3">
                {loading && <Spinner size="md" text="Đang tải..." center />}
                {error && <Alert variant="error">{error}</Alert>}
                {!loading && !error && entries.length === 0 && (
                    <div className="text-center py-8 text-sm text-gray-500">Chưa có lịch sử.</div>
                )}
                {!loading && !error && entries.length > 0 && (
                    <ul className="space-y-2">
                        {entries.map((e) => {
                            const badge = ACTION_BADGES[e.action] || { variant: 'secondary', label: e.action };
                            const isOpen = !!expanded[e.id];
                            return (
                                <li key={e.id} className="border border-gray-200 rounded-md bg-white">
                                    <button
                                        type="button"
                                        onClick={() => toggle(e.id)}
                                        className="w-full px-3 py-2 flex items-center gap-3 text-left hover:bg-gray-50"
                                    >
                                        <Badge variant={badge.variant}>{badge.label}</Badge>
                                        <span className="text-xs text-gray-700">{e.actor || '—'}</span>
                                        <span className="text-xs text-gray-400 ml-auto">
                                            {e.changed_at ? new Date(e.changed_at).toLocaleString('vi-VN') : '—'}
                                        </span>
                                        <span className="text-xs text-indigo-500">{isOpen ? 'Ẩn' : 'Xem diff'}</span>
                                    </button>
                                    {isOpen && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 border-t border-gray-100 p-3">
                                            <DiffPane label="Trước" payload={e.before} />
                                            <DiffPane label="Sau" payload={e.after} />
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
                <div className="flex justify-end pt-3 border-t border-gray-100">
                    <Button variant="secondary" onClick={onClose}>Đóng</Button>
                </div>
            </div>
        </Modal>
    );
}


function DiffPane({ label, payload }) {
    return (
        <div>
            <p className="text-[11px] font-bold tracking-wide text-gray-400 uppercase mb-1">{label}</p>
            <pre className="text-[11px] font-mono leading-snug bg-gray-50 border border-gray-100 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-60">
                {payload === null || payload === undefined
                    ? '— (rỗng)'
                    : JSON.stringify(payload, null, 2)}
            </pre>
        </div>
    );
}


function RuleEditorModal({ initial, onSave, onClose }) {
    const [form, setForm] = useState(initial);
    const [mode, setMode] = useState('visual');
    const [jsonText, setJsonText] = useState(() => JSON.stringify(initial.predicate, null, 2));
    const [jsonError, setJsonError] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewResult, setPreviewResult] = useState(null);
    const [previewError, setPreviewError] = useState(null);
    const [schema, setSchema] = useState(null);
    const [schemaError, setSchemaError] = useState(null);
    const [schemaRefreshing, setSchemaRefreshing] = useState(false);
    const isCreate = form._mode === 'create';

    const loadSchema = async (refresh = false) => {
        try {
            setSchemaError(null);
            if (refresh) setSchemaRefreshing(true);
            const data = await adminApi.getFeedVisibilitySchema({ refresh });
            const fields = Array.isArray(data?.fields) ? data.fields : [];
            setSchema({ fields, fieldByName: buildFieldByName(fields), operators: data?.operators || {} });
        } catch (e) {
            setSchemaError(e?.message || 'Không tải được schema');
        } finally {
            setSchemaRefreshing(false);
        }
    };

    useEffect(() => {
        loadSchema(false);
    }, []);

    const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

    const setPredicate = (next) => {
        setForm((prev) => ({ ...prev, predicate: next }));
        setJsonText(JSON.stringify(next, null, 2));
        setJsonError(null);
        setPreviewResult(null);
    };

    const handleJsonChange = (text) => {
        setJsonText(text);
        try {
            const parsed = JSON.parse(text);
            setForm((prev) => ({ ...prev, predicate: parsed }));
            setJsonError(null);
            setPreviewResult(null);
        } catch (err) {
            setJsonError(err?.message || 'JSON không hợp lệ');
        }
    };

    const handlePreview = async () => {
        setPreviewLoading(true);
        setPreviewError(null);
        setPreviewResult(null);
        try {
            const result = await adminApi.previewFeedVisibilityRule(form.predicate, 10);
            setPreviewResult(result);
        } catch (e) {
            setPreviewError(e?.message || 'Preview lỗi');
        } finally {
            setPreviewLoading(false);
        }
    };

    const nameError = form.name.trim() ? null : 'Tên rule bắt buộc.';
    const hasError = !!(nameError || jsonError);

    return (
        <Modal
            open
            title={isCreate ? 'Tạo rule mới' : `Chỉnh sửa — ${form.name}`}
            onClose={onClose}
            size="xl"
        >
            <div className="p-5 space-y-4">
                <div>
                    <Input
                        label="Tên rule"
                        value={form.name}
                        onChange={(e) => set('name', e.target.value)}
                        placeholder="published-equity-only"
                    />
                    {nameError && <p className="text-xs text-red-500 mt-1">{nameError}</p>}
                </div>
                <Textarea
                    label="Mô tả"
                    rows={2}
                    value={form.description}
                    onChange={(e) => set('description', e.target.value)}
                    placeholder="Chỉ hiển thị insight equity đã publish, severity ≥ MEDIUM, ≤ 30 ngày."
                />

                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700">Predicate</label>
                        <div className="flex gap-1 text-xs">
                            <button type="button"
                                className={`px-2 py-1 rounded ${mode === 'visual' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-100'}`}
                                onClick={() => setMode('visual')}>
                                Visual
                            </button>
                            <button type="button"
                                className={`px-2 py-1 rounded ${mode === 'json' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-100'}`}
                                onClick={() => setMode('json')}>
                                JSON
                            </button>
                        </div>
                    </div>

                    {mode === 'visual' ? (
                        schema ? (
                            <SchemaContext.Provider value={schema}>
                                <div className="flex items-center justify-end mb-2">
                                    <button
                                        type="button"
                                        onClick={() => loadSchema(true)}
                                        disabled={schemaRefreshing}
                                        className="text-xs text-indigo-600 hover:underline disabled:text-gray-400"
                                    >
                                        {schemaRefreshing ? 'Đang refresh...' : 'Refresh schema từ DB'}
                                    </button>
                                </div>
                                <div className="border border-gray-200 rounded-md p-3 bg-gray-50/60">
                                    <PredicateNode
                                        node={form.predicate}
                                        onChange={setPredicate}
                                        isRoot
                                    />
                                </div>
                            </SchemaContext.Provider>
                        ) : (
                            <div className="border border-gray-200 rounded-md p-6 bg-gray-50/60 text-xs text-gray-500 text-center">
                                {schemaError ? `Lỗi: ${schemaError}` : 'Đang tải schema...'}
                            </div>
                        )
                    ) : (
                        <div>
                            <textarea
                                value={jsonText}
                                onChange={(e) => handleJsonChange(e.target.value)}
                                rows={10}
                                spellCheck={false}
                                className="w-full font-mono text-xs border border-gray-200 rounded-md p-3 bg-white"
                            />
                            {jsonError && <p className="text-xs text-red-500 mt-1">{jsonError}</p>}
                        </div>
                    )}
                </div>

                <div className="border-t border-gray-100 pt-4">
                    <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700">Preview (dry-run)</label>
                        <Button
                            variant="secondary"
                            size="xs"
                            onClick={handlePreview}
                            disabled={!!jsonError || previewLoading}
                        >
                            {previewLoading ? 'Đang chạy...' : 'Chạy preview'}
                        </Button>
                    </div>
                    {previewError && <p className="text-xs text-red-500">{previewError}</p>}
                    {previewResult && (
                        <div className="text-xs">
                            <p className="text-gray-700 mb-2">
                                Match <span className="font-bold text-indigo-700">{previewResult.total}</span> rows.
                                Sample {previewResult.sample.length}:
                            </p>
                            <div className="border border-gray-200 rounded bg-white max-h-48 overflow-y-auto">
                                {previewResult.sample.length === 0 ? (
                                    <p className="px-3 py-2 text-gray-500">Không có row nào match.</p>
                                ) : (
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="border-b border-gray-100 bg-gray-50/60">
                                                <th className="text-left px-2 py-1 font-medium text-gray-500">Symbol</th>
                                                <th className="text-left px-2 py-1 font-medium text-gray-500">Category</th>
                                                <th className="text-left px-2 py-1 font-medium text-gray-500">Status</th>
                                                <th className="text-left px-2 py-1 font-medium text-gray-500">Severity</th>
                                                <th className="text-left px-2 py-1 font-medium text-gray-500">Snapshot</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {previewResult.sample.map((row) => (
                                                <tr key={row.id} className="border-t border-gray-100">
                                                    <td className="px-2 py-1 font-mono">{row.symbol}</td>
                                                    <td className="px-2 py-1">{row.category}</td>
                                                    <td className="px-2 py-1">{row.status}</td>
                                                    <td className="px-2 py-1">{row.severity || '-'}</td>
                                                    <td className="px-2 py-1 text-gray-500">
                                                        {new Date(row.snapshot_at).toLocaleString('vi-VN')}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {isCreate && (
                    <div className="flex items-center gap-2 border-t border-gray-100 pt-3">
                        <input
                            type="checkbox"
                            id="activate-now"
                            checked={!!form.activate}
                            onChange={(e) => set('activate', e.target.checked)}
                        />
                        <label htmlFor="activate-now" className="text-sm text-gray-700">
                            Kích hoạt ngay sau khi tạo
                        </label>
                        <span className="text-xs text-gray-400 ml-2">
                            Rule active hiện tại sẽ bị deactivate.
                        </span>
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                    <Button variant="secondary" onClick={onClose}>Huỷ</Button>
                    <Button disabled={hasError} onClick={() => onSave(form)}>
                        {isCreate ? 'Tạo' : 'Lưu'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}


function PredicateNode({ node, onChange, isRoot = false }) {
    const schema = useContext(SchemaContext);
    const op = node?.op;

    if (op === 'and' || op === 'or') {
        const args = Array.isArray(node.args) ? node.args : [];
        return (
            <div className={`${isRoot ? '' : 'border-l-2 border-indigo-200 pl-3'} space-y-2`}>
                <div className="flex items-center gap-2">
                    <select
                        value={op}
                        onChange={(e) => onChange({ ...node, op: e.target.value })}
                        className="text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 rounded px-2 py-1"
                    >
                        <option value="and">AND</option>
                        <option value="or">OR</option>
                    </select>
                    <button
                        type="button"
                        className="text-xs text-indigo-600 hover:underline"
                        onClick={() => onChange({ ...node, args: [...args, defaultLeaf(schema)] })}
                    >
                        + điều kiện
                    </button>
                    <button
                        type="button"
                        className="text-xs text-indigo-600 hover:underline"
                        onClick={() => onChange({ ...node, args: [...args, defaultGroup('and')] })}
                    >
                        + nhóm AND
                    </button>
                    <button
                        type="button"
                        className="text-xs text-indigo-600 hover:underline"
                        onClick={() => onChange({ ...node, args: [...args, { op: 'not', args: [defaultLeaf(schema)] }] })}
                    >
                        + NOT
                    </button>
                    {!isRoot && (
                        <button
                            type="button"
                            className="text-xs text-red-500 hover:underline ml-auto"
                            onClick={() => onChange(null)}
                        >
                            Xoá
                        </button>
                    )}
                </div>
                {args.length === 0 && (
                    <p className="text-xs text-gray-400 italic">
                        {op === 'and' ? 'AND rỗng = match tất cả' : 'OR rỗng = match không gì'}
                    </p>
                )}
                {args.map((child, idx) => (
                    <PredicateNode
                        key={idx}
                        node={child}
                        onChange={(next) => {
                            const nextArgs = next === null
                                ? args.filter((_, i) => i !== idx)
                                : args.map((c, i) => (i === idx ? next : c));
                            onChange({ ...node, args: nextArgs });
                        }}
                    />
                ))}
            </div>
        );
    }

    if (op === 'not') {
        const child = node.args?.[0];
        return (
            <div className="border-l-2 border-amber-200 pl-3">
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 rounded px-2 py-1">NOT</span>
                    {!isRoot && (
                        <button
                            type="button"
                            className="text-xs text-red-500 hover:underline ml-auto"
                            onClick={() => onChange(null)}
                        >
                            Xoá
                        </button>
                    )}
                </div>
                {child && (
                    <PredicateNode
                        node={child}
                        onChange={(next) => onChange({ ...node, args: [next] })}
                    />
                )}
            </div>
        );
    }

    return <LeafNode node={node} onChange={onChange} />;
}


function LeafNode({ node, onChange }) {
    const schema = useContext(SchemaContext);
    const fields = schema.fields || [];
    const fieldSpec = schema.fieldByName[node.field] || fields[0];
    const allowedOps = fieldSpec?.allowed_ops || ['eq'];
    const operatorLabels = schema.operators || {};

    const handleFieldChange = (newField) => {
        const newSpec = schema.fieldByName[newField];
        const newOps = newSpec?.allowed_ops || ['eq'];
        onChange({
            op: newOps.includes(node.op) ? node.op : newOps[0],
            field: newField,
            value: '',
        });
    };

    const handleOpChange = (newOp) => {
        let nextValue = node.value;
        if (newOp === 'in' || newOp === 'nin') {
            nextValue = Array.isArray(node.value) ? node.value : [node.value].filter(Boolean);
        } else if (newOp === 'between') {
            nextValue = Array.isArray(node.value) && node.value.length === 2 ? node.value : ['', ''];
        } else if (Array.isArray(node.value)) {
            nextValue = node.value[0] || '';
        }
        onChange({ ...node, op: newOp, value: nextValue });
    };

    if (!fieldSpec) {
        return <div className="text-xs text-gray-500 italic">Schema chưa sẵn sàng.</div>;
    }

    return (
        <div className="flex items-start gap-2 flex-wrap bg-white border border-gray-200 rounded p-2">
            <select
                value={node.field}
                onChange={(e) => handleFieldChange(e.target.value)}
                className="text-xs border border-gray-200 rounded px-2 py-1"
            >
                {fields.map((f) => (
                    <option key={f.name} value={f.name}>{f.label || f.name}</option>
                ))}
            </select>
            <select
                value={node.op}
                onChange={(e) => handleOpChange(e.target.value)}
                className="text-xs border border-gray-200 rounded px-2 py-1"
            >
                {allowedOps.map((op) => (
                    <option key={op} value={op}>{operatorLabels[op]?.label || op}</option>
                ))}
            </select>
            <ValueInput
                spec={fieldSpec}
                op={node.op}
                value={node.value}
                onChange={(v) => onChange({ ...node, value: v })}
            />
            <button
                type="button"
                className="text-xs text-red-500 hover:underline ml-auto"
                onClick={() => onChange(null)}
            >
                Xoá
            </button>
        </div>
    );
}


function useSuggestionsForSpec(spec) {
    return Array.isArray(spec?.enums) ? spec.enums : [];
}


function ValueInput({ spec, op, value, onChange }) {
    if (op === 'in' || op === 'nin') {
        const list = Array.isArray(value) ? value : [];
        return (
            <TagsAutocomplete
                spec={spec}
                tags={list}
                onChange={(tags) => onChange(spec.type === 'int' ? tags.map(Number) : tags)}
            />
        );
    }
    if (op === 'between') {
        const pair = Array.isArray(value) && value.length === 2 ? value : ['', ''];
        return (
            <div className="flex gap-1 items-center">
                <input
                    type={spec.type === 'datetime' ? 'datetime-local' : (spec.type === 'int' ? 'number' : 'text')}
                    value={pair[0] ?? ''}
                    onChange={(e) => onChange([
                        spec.type === 'int' ? Number(e.target.value) : e.target.value,
                        pair[1],
                    ])}
                    className="text-xs border border-gray-200 rounded px-2 py-1 w-32"
                />
                <span className="text-xs text-gray-400">→</span>
                <input
                    type={spec.type === 'datetime' ? 'datetime-local' : (spec.type === 'int' ? 'number' : 'text')}
                    value={pair[1] ?? ''}
                    onChange={(e) => onChange([
                        pair[0],
                        spec.type === 'int' ? Number(e.target.value) : e.target.value,
                    ])}
                    className="text-xs border border-gray-200 rounded px-2 py-1 w-32"
                />
            </div>
        );
    }

    return (
        <SingleAutocompleteInput
            spec={spec}
            value={value}
            onChange={onChange}
        />
    );
}


function SingleAutocompleteInput({ spec, value, onChange }) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const suggestions = useSuggestionsForSpec(spec) || [];
    const inputType = spec.type === 'datetime' ? 'datetime-local' : (spec.type === 'int' ? 'number' : 'text');
    const filtered = useMemo(() => {
        if (!suggestions.length) return [];
        const q = (value ?? '').toString().trim().toLowerCase();
        if (!q) return suggestions.slice(0, 8);
        return suggestions
            .filter((s) => s.toString().toLowerCase().includes(q) && s !== value)
            .slice(0, 8);
    }, [suggestions, value]);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const showDropdown = open && suggestions.length > 0 && filtered.length > 0;

    return (
        <div ref={containerRef} className="relative flex-1 min-w-[200px]">
            <input
                type={inputType}
                value={value ?? ''}
                onChange={(e) => {
                    onChange(spec.type === 'int' ? Number(e.target.value) : e.target.value);
                    setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                className="w-full text-xs border border-gray-200 rounded px-2 py-1"
                placeholder={spec.type === 'datetime' ? '2026-01-01T00:00' : (spec.type === 'int' ? '30' : 'value')}
            />
            {showDropdown && (
                <ul className="absolute left-0 right-0 z-10 mt-1 max-h-48 overflow-y-auto rounded border border-gray-200 bg-white shadow-md text-xs">
                    {filtered.map((s) => (
                        <li
                            key={s}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                onChange(spec.type === 'int' ? Number(s) : s);
                                setOpen(false);
                            }}
                            className="px-2 py-1 cursor-pointer hover:bg-indigo-50"
                        >
                            {s}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}


function TagsAutocomplete({ spec, tags, onChange }) {
    const [draft, setDraft] = useState('');
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const suggestions = useSuggestionsForSpec(spec) || [];
    const filtered = useMemo(() => {
        const q = draft.trim().toLowerCase();
        if (!q) return suggestions.filter((s) => !tags.includes(s)).slice(0, 8);
        return suggestions
            .filter((s) => !tags.includes(s) && s.toLowerCase().includes(q))
            .slice(0, 8);
    }, [draft, suggestions, tags]);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const addTag = (raw) => {
        const v = (raw ?? draft).toString().trim();
        if (!v) return;
        if (tags.includes(v)) {
            setDraft('');
            return;
        }
        onChange([...tags, v]);
        setDraft('');
    };

    const removeTag = (t) => onChange(tags.filter((x) => x !== t));

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addTag();
        } else if (e.key === ',') {
            e.preventDefault();
            addTag();
        } else if (e.key === 'Backspace' && !draft && tags.length) {
            removeTag(tags[tags.length - 1]);
        }
    };

    return (
        <div ref={containerRef} className="relative flex-1 min-w-[200px]">
            <div
                onClick={() => setOpen(true)}
                className="flex flex-wrap gap-1 rounded border border-gray-200 bg-white px-1.5 py-1 cursor-text focus-within:ring-1 focus-within:ring-indigo-400 focus-within:border-indigo-400"
            >
                {tags.map((t) => (
                    <span
                        key={t}
                        className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700"
                    >
                        {t}
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeTag(t); }}
                            className="text-indigo-500 hover:text-indigo-800"
                            aria-label={`Remove ${t}`}
                        >
                            ×
                        </button>
                    </span>
                ))}
                <input
                    type="text"
                    value={draft}
                    onChange={(e) => { setDraft(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={handleKeyDown}
                    onBlur={() => addTag()}
                    placeholder={tags.length ? '' : 'Nhập rồi Enter'}
                    className="flex-1 min-w-[100px] border-0 p-0 text-xs focus:ring-0 focus:outline-none bg-transparent"
                />
            </div>
            {open && filtered.length > 0 && (
                <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded border border-gray-200 bg-white shadow-md text-xs">
                    {filtered.map((s) => (
                        <li
                            key={s}
                            onMouseDown={(e) => { e.preventDefault(); addTag(s); }}
                            className="px-2 py-1 cursor-pointer hover:bg-indigo-50"
                        >
                            {s}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
